import {Group, Paper, Radio, Stack, Text, Tooltip} from '@mantine/core';
import React from 'react';

import {handleSelectableCardKeyDown} from './selectableCardKeyboard';

interface SelectableRadioCardProps {
  cardClassName: string;
  description?: string;
  disabled: boolean;
  label: string;
  onSelect: () => void;
  showTooltip: boolean;
  tooltipLabel?: string;
  value: string;
  children?: React.ReactNode;
  // Optional content rendered at the far right of the radio row, e.g. a lock icon for a
  // released-but-not-enabled option.
  rightSection?: React.ReactNode;
}

export default function SelectableRadioCard({
  cardClassName,
  description,
  disabled,
  label,
  onSelect,
  showTooltip,
  tooltipLabel,
  value,
  children,
  rightSection,
}: SelectableRadioCardProps): JSX.Element {
  const mainContent = (
    <Stack spacing="sm">
      <Radio value={value} label={label} disabled={disabled} size="lg" />
      {description != null && (
        <Text size="md" c="dimmed">
          {description}
        </Text>
      )}
      {children}
    </Stack>
  );
  const card = (
    <Paper
      withBorder={true}
      p="sm"
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={() => {
        if (!disabled) {
          onSelect();
        }
      }}
      onKeyDown={(event) => {
        handleSelectableCardKeyDown(event, onSelect, disabled);
      }}
      className={cardClassName}
    >
      {rightSection != null ? (
        // Center the right-hand adornment (e.g. a lock icon) vertically over the whole card.
        <Group position="apart" align="center" noWrap={true} spacing="sm">
          {mainContent}
          {rightSection}
        </Group>
      ) : (
        mainContent
      )}
    </Paper>
  );

  if (!showTooltip || tooltipLabel == null) {
    return card;
  }
  return <Tooltip label={tooltipLabel}>{card}</Tooltip>;
}
